import { AbilityBuilder } from '@casl/ability'
import getDisplayName from 'react-display-name'
import _ from 'lodash'

const shareAbilities = (user, { event }, can) => {
  can('read', 'HomePage')
  can('read', 'ProfilePage')

  if ((event.afterMainTour || event.afterPreparatoryTour) && event.isAwardsAvailable) {
    can('read', 'scoring')
    can('read', 'Diplomas')
    can('read', 'DiplomasPage')
  }

  if (!event.beforeTrialTour && !event.atTrialTour) {
    can('read', 'feedback')
  }
}

export function defineTeacherAbilities(user, { event }) {
  return AbilityBuilder.define((can, cannot) => {
    shareAbilities(user, { event }, can)

    can('read', 'ClassesPage')
    can('read', 'LearnersPage')
    can('read', 'AddClassLearnersPage')
    can('read', 'OlympiadPage')
    can('read', 'preparatoryCards')
    can('read', 'trialCards')
    can('read', 'TaskPage')

    can('update', 'Group', { owner: +user.id })

    cannot('update', 'profile')
    cannot('read', 'ProfileEditorPage')

    if (window.COUNTRY === 'IN' || window.COUNTRY === 'ZA' || window.COUNTRY === 'CN') {
      can('update', 'profile')
      can('read', 'ProfileEditorPage')
    }
    if (event.afterMainTour) {
      can('read', 'mainCards')
      can('read', 'AnswerPage', { tour: 'main' })
      can('read', 'Card', { tour: 'main' })
    }

    can('create', 'Class')
    can('read', 'CreateClassPage')

    if (!event.beforePreparatoryTour && !event.afterMainTour) {
      can('read', 'StatisticsPage')
    }

    if (!event.beforePreparatoryTour) {
      can('read', 'TasksPage')
    }

    if (event.atMainTour || event.afterMainTour) {
      can('read', 'Statistic', 'main')
      can('read', 'LeadersPage', 'main')
    }

    if (event.atPreparatoryTour || event.afterPreparatoryTour) {
      can('read', 'Statistic', 'preparatory')
    }

    if (!event.preparatoryTour && !event.beforeTrialTour) {
      if (_.includes(event.availableTours, 'trial')) {
        can('read', 'Statistic', 'trial')
      }
    }
  })
}

export function defineParentAbilities(user, { event }) {
  return AbilityBuilder.define((can, cannot) => {
    shareAbilities(user, { event }, can)

    can('read', 'ChildrenPage')
    can('read', 'AddChildPage')

    cannot('update', 'profile')
    cannot('read', 'ProfileEditorPage')

    if (!event.beforePreparatoryTour) {
      can('read', 'StatisticsPage')
      can('read', 'TasksPage')
    }

    if (event.atMainTour || event.afterMainTour) {
      can('read', 'Statistic', 'main')
    }

    if (event.atPreparatoryTour || event.afterPreparatoryTour) {
      can('read', 'Statistic', 'preparatory')
    }

    if (!event.preparatoryTour && !event.beforeTrialTour) {
      can('read', 'Statistic', 'trial')
    }

    if (window.COUNTRY == 'IN' || window.COUNTRY === 'ZA' || window.COUNTRY === 'CN') {
      can('update', 'profile')
      can('read', 'ProfileEditorPage')
    }
  })
}

export function defineStudentAbilities(user, { event }) {
  return AbilityBuilder.define(can => {
    shareAbilities(user, { event }, can)

    if (!!user.participation.olympiad) {
      can('participate', 'Olympiad')

      if (event.beforeTrialTour || event.beforePreparatoryTour) {
        can('read', 'ComingSoonPage')
      }

      if (event.atTrialTour || user.participation.isSolving) {
        can('read', 'RatingsPage')
      }

      if (event.atPreparatoryTour) {
        can('read', 'AnswerPage', { tour: 'preparatory' })

        if (user.participation.startedAt) {
          can('read', 'PreparatoryTasksPage')
          can('read', 'TaskPage')
          can('solve', 'Tour', 'preparatory')
          can('read', 'AnswerPage', { tour: 'preparatory' })
        }

        if (user.participation.canBeStarted) {
          can('read', 'StartPage')
          can('start', 'Tour', 'preparatory')
        }
      }

      if (event.atTrialTour) {
        can('read', 'AnswerPage', { tour: 'trial' })

        if (user.participation.startedAt) {
          can('read', 'TrialTasksPage')
          can('read', 'TaskPage')
          can('solve', 'Tour', 'trial')
          can('read', 'AnswerPage', { tour: 'trial' })
        }

        if (user.participation.canBeStarted) {
          can('read', 'StartPage')
          can('start', 'Tour', 'trial')
        }
      }

      if (event.atMainTour || event.afterMainTour) {
        can('read', 'AnswerPage', { tour: 'main' })

        if (user.participation.isSolving) {
          can('read', 'MainTasksPage')
          can('read', 'TaskPage')
          can('solve', 'Tour', 'main')
        }

        if (user.participation.canBeStarted) {
          can('read', 'MainIntroPage')
          can('read', 'ConfirmPage')
          can('read', 'StartPage')
          can('start', 'Tour', 'main')
        }
      }
    }

    else {
      can('read', 'InappropriateGradePage')
    }

    if (event.afterPreparatoryTour) {
      can('read', 'Score', 'preparatory')
      can('read', 'Score', 'correctAnswer')
      can('read', 'Score', 'rewarding')
      can('read', 'RewardingPage')
    }

    if (event.atTrialTour && window.COUNTRY === 'VN') {
      can('read', 'ResultsPageOfTrial')
    }

    if (event.atMainTour && user.participation.hasSolved) {
      can('read', 'ResultsPage')
      can('read', 'Score', 'main')

      if (user.participation.canBeRestarted) {
        can('restart', 'Olympiad')
      }
    }

    if (event.afterMainTour) {
      if (!user.participation.canBeStarted && !user.participation.isSolving) {
        can('read', 'Score', 'main')
        can('read', 'Score', 'correctAnswer')
        can('read', 'Score', 'rewarding')
        can('read', 'RewardingPage')
        can('read', 'SolutionPage')
      }

      if (event.isAwardsAvailable) {
        can('read', 'Award')
      }
    }
  })
}

export function definePublicAbilities(session) {
  return AbilityBuilder.define((can, cannot) => {
    can('read', 'all')

    cannot('read', 'ChildrenPage')
    cannot('read', 'CompletedPage')
    cannot('read', 'LearnersPage')
    cannot('read', 'CreateClassPage')
    cannot('read', 'CompletedPage')

    if (session.isLoggedIn) {
      cannot('read', 'RegistrationPage')

      if (session.user.isParent) {
        can('read', 'ChildrenPage')
        can('read', 'AddChildPage')
        can('read', 'CompletedPage')
      }

      if (session.user.isTeacher) {
        can('read', 'LearnersPage')
        can('read', 'CreateClassPage')
        can('read', 'CompletedPage')
      }
    }
  })
}

export function authorize(ability, component) {
  console.debug(`Verify access to '${getDisplayName(component)}Page'.`)
  return ability.can('read', `${getDisplayName(component)}Page`)
}
